import { useEffect, useRef } from 'react';

/**
 * CanvasContextMenu - Context menu for right-click actions on the canvas.
 *
 * @param {boolean} visible - Whether the menu is visible
 * @param {number} x - Mouse X position (relative to canvas container)
 * @param {number} y - Mouse Y position (relative to canvas container)
 * @param {Array} items - Menu items: { label, shortcut?, onClick, disabled?, separator? }
 * @param {Function} onClose - Called when the menu should close
 */
const CanvasContextMenu = ({ visible, x, y, items = [], onClose }) => {
  const menuRef = useRef(null);

  // Close on click outside or Escape
  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };

    const handleMouseDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };

    const handleScroll = () => onClose();

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [visible, onClose]);

  if (!visible) return null;

  // Boundary check: flip menu left/up if it would overflow viewport
  const MENU_WIDTH = 220;
  const MENU_HEIGHT_APPROX = items.length * 32 + 16;

  const adjustedX = x + MENU_WIDTH > window.innerWidth ? x - MENU_WIDTH : x;
  const adjustedY = y + MENU_HEIGHT_APPROX > window.innerHeight ? y - MENU_HEIGHT_APPROX : y;

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label="Kontextmenü"
      tabIndex={-1}
      style={{
        position: 'absolute',
        left: adjustedX,
        top: adjustedY,
        zIndex: 1000,
        minWidth: `${MENU_WIDTH}px`,
      }}
      className="bg-white shadow-lg rounded-md border border-gray-200 py-1"
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((item, index) => {
        if (item.separator) {
          return (
            <hr
              key={index}
              className="border-t border-gray-200 my-1"
            />
          );
        }

        return (
          <button
            key={index}
            role="menuitem"
            disabled={item.disabled}
            onClick={() => {
              if (!item.disabled) {
                item.onClick?.();
                onClose();
              }
            }}
            className={[
              'w-full flex items-center justify-between px-3 py-1.5 text-sm text-left',
              item.disabled
                ? 'text-gray-400 cursor-default'
                : 'text-gray-900 hover:bg-gray-100 cursor-pointer',
            ].join(' ')}
          >
            <span>{item.label}</span>
            {item.shortcut && (
              <span className="text-gray-500 text-xs ml-4">{item.shortcut}</span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default CanvasContextMenu;
